import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { navLinks } from "@/mocks/homeData";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const languages = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
    { code: "de", label: "Deutsch", flag: "🇩🇪" },
  ];

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLabels: Record<string, string> = {
    "/": "nav.home",
    "/blog": "nav.blog",
    "/partners": "nav.partners",
    "/contact": "nav.contact",
  };

  const linkColor = scrolled ? "text-background-50/70 hover:text-primary-400" : "text-background-50/80 hover:text-primary-400";

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-950"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            onClick={scrollToTop}
            className="flex flex-col items-start cursor-pointer"
          >
            <span
              className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight whitespace-nowrap leading-none bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, #ffffff 0%, #eef1f7 20%, #c9d0de 45%, #9aa3b8 65%, #d9dfeb 82%, #f6f8fc 100%)",
              }}
            >
              Clefs de Lys SA
            </span>
            <span
              className="inline-block h-[2px] md:h-[3px] rounded-full mt-1 w-full"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, #ffffff 0%, #c9d0de 50%, #f6f8fc 100%)",
              }}
            ></span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => link.href === "/" && scrollToTop()}
                className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${linkColor}`}
              >
                {t(navLabels[link.href] || link.label)}
              </Link>
            ))}

            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${linkColor}`}
              >
                <span>{currentLang.flag}</span>
                <span className="hidden lg:inline">{currentLang.code.toUpperCase()}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? "rotate-180" : ""}`}></i>
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 py-2 rounded-lg shadow-lg min-w-[140px] bg-background-800 border border-background-700">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap cursor-pointer transition-colors ${
                        i18n.language === lang.code
                          ? "text-primary-400 font-medium"
                          : "text-background-50/70 hover:bg-background-700"
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="md:hidden flex items-center gap-1">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => i18n.changeLanguage(lang.code)}
                title={lang.label}
                aria-label={lang.label}
                className={`h-6 min-w-[30px] px-1.5 flex items-center justify-center rounded-md text-[11px] font-bold tracking-wide whitespace-nowrap cursor-pointer transition-colors ${
                  i18n.language === lang.code
                    ? "bg-primary-500 text-background-950"
                    : "bg-background-50/10 text-background-50/90 hover:bg-background-50/20"
                }`}
              >
                {lang.code.toUpperCase()}
              </button>
            ))}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-10 h-10 flex items-center justify-center cursor-pointer text-background-50"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-950 border-t border-background-800">
          <div className="px-4 py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => {
                  setMobileOpen(false);
                  if (link.href === "/") scrollToTop();
                }}
                className="text-background-50/70 text-sm font-medium py-2 cursor-pointer hover:text-primary-400 transition-colors"
              >
                {t(navLabels[link.href] || link.label)}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}